import * as React from 'react';
import { observer } from 'mobx-react';
import { injectStripe } from 'react-stripe-elements';
import CardSection from './CardSection';
import { Button, Alert, Row, Col } from 'reactstrap';
import { translate } from 'react-i18next';
import PaymentService from '../Services/PaymentService';

@observer
class CheckoutForm extends React.Component {
  formRef = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      error: null,
      submitting: false,
    };
  }
  handleSubmit = async (ev) => {
    ev.preventDefault();
  
    // Prevent duplicate submissions
    if (this.state.submitting) {
      return;
    }
  
    if (this.props.stripe && this.props.elements) {
      this.setState({ submitting: true, error: null });
      
      try {
        const booking = this.props.booking;
        
        if (!booking) {
          throw new Error('Booking information is missing');
        }
        
        const firstName = (booking && booking.first_name) ? booking.first_name : ''; 
        const lastName = (booking && booking.last_name) ? booking.last_name : '';  
        const fullName = `${firstName} ${lastName}`.trim();
        
        // Get card element
        const cardElement = this.props.elements.getElement('card');
        
        if (!cardElement) {
          throw new Error('Card element not found');
        }
        
        // Get payment intent from backend
        const paymentIntentResponse = await PaymentService.getPaymentIntentForBooking(booking);
        
        if (!paymentIntentResponse || !paymentIntentResponse.client_secret) {
          throw new Error('Failed to create payment intent - no client secret received');
        }
        
        // Update UI to show 3D Secure might be required
        this.setState({ 
          submitting: true, 
          error: null 
        });
        
        // Confirm payment with 3D Secure support using react-stripe-elements
        const {error, paymentIntent: confirmedPaymentIntent} = await this.props.stripe.confirmCardPayment(
          paymentIntentResponse.client_secret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: fullName || 'Guest',
                email: booking.email || '',
              },
            }
          }
        );
        
        if (error) {
          // Log detailed error information
          console.error('[Stripe Error Details]', {
            code: error.code,
            type: error.type,
            message: error.message,
            decline_code: error.decline_code,
            payment_intent: error.payment_intent
          });
          
          // Provide user-friendly error messages
          let userMessage = error.message;
          if (error.code === 'card_declined') {
            userMessage = 'Your card was declined. Please try a different card or contact your bank.';
          } else if (error.code === 'insufficient_funds') {
            userMessage = 'Insufficient funds on your card. Please try a different card.';
          } else if (error.code === 'authentication_required') {
            userMessage = '3D Secure authentication failed. Please try again.';
          }
          
          this.setState({
            error: `Payment failed: ${userMessage}`,
            submitting: false,
          });
          console.error('[Payment Error]', error);
        } else if (confirmedPaymentIntent.status === 'succeeded') {
          // Payment succeeded, including 3D Secure if required
          
          // Set the payment token on the booking and trigger the normal booking flow
          // The backend will handle creating both the booking and payment records
          this.setState({ submitting: false });
          
          if (this.props.handlePayment) {
            this.props.handlePayment(confirmedPaymentIntent);
          }
        } else if (confirmedPaymentIntent.status === 'requires_action') {
          // This should not happen with react-stripe-elements as it handles actions automatically
          this.setState({
            error: 'Payment requires additional verification. Please try again.',
            submitting: false,
          });
          console.error('[Payment Status]', 'requires_action after confirmCardPayment');
        } else {
          this.setState({
            error: `Payment failed with status: ${confirmedPaymentIntent.status}`,
            submitting: false,
          });
          console.error('[Payment Status]', confirmedPaymentIntent.status);
        }
      } catch (error) {
        console.error('[Payment Processing Error]', error);
        this.setState({
          error: `Payment processing failed: ${error.message}`,
          submitting: false,
        });
      } finally {
        // Ensure submitting is always reset, even if there's an unexpected error
        if (this.state.submitting) {
          this.setState({ submitting: false });
        }
      }
    } else {
      this.setState({
        error: 'Stripe is not loaded properly. Please refresh the page.',
        submitting: false,
      });
    }
  };
  
  

  submitForm = () => {
    if (this.formRef.current) {
      this.formRef.current.submit();
    }
  };

  render() {
    const { t, booking, showSubmitButton = true } = this.props;
    const { error, submitting } = this.state;

    return (
      <form id="bookingForm" onSubmit={this.handleSubmit}>
        <CardSection />
        {
          error && <Row><Col lg={12}><Alert color='danger'>{error}</Alert></Col></Row>
        }
        {showSubmitButton && (
          <Button
            color='primary'
            block
            disabled={booking.invalid || submitting}
          >
            {submitting ? t('Processing payment...') : (this.props.buttonText || t('Book'))}
          </Button>
        )}
      </form>
    );
  }
}

export default translate()(injectStripe(CheckoutForm));



// WEBPACK FOOTER //
// ./src/App/Checkout/CheckoutForm.js